import React, { useState } from "react";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { Input } from "../../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../../design/fieldset";
import { useContact } from "../../../../contexts/ContactContext";
import { Switch, SwitchField } from "../../../../design/switch";
import PhoneNumberInput from "../../../../components/PhoneNumberInput";
import { useParent } from "../../../../contexts/ParentContext";

interface Props {
  parentId: string;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  role?: string;
  saveError?: string;
}

const CreateContactButton: React.FC<Props> = ({ parentId }) => {
  const { createContact } = useContact();
  const { setSelectedParent } = useParent();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    role: "",
    phoneNumber: "",
    email: "",
    isVisibleToSupervisor: false,
    isEmergencyContact: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required.";
    if (!formData.role.trim()) newErrors.firstName = "Description is required.";
    if (formData.phoneNumber && !/^\+?[0-9]{10,15}$/.test(formData.phoneNumber))
      newErrors.phoneNumber = "Invalid phone number.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email address.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      await createContact({
        ...formData,
        phoneNumber: `+${formData.phoneNumber.replace(/\D/g, "")}`, // Remove non-digit characters
        parentId,
      });
      setSelectedParent(parentId);
      clearForm();
    } catch (error) {
      setErrors({ saveError: (error as Error).message });
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      role: "",
      phoneNumber: "",
      email: "",
      isVisibleToSupervisor: false,
      isEmergencyContact: false,
    });
    setIsOpen(false);
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create Contact
      </Button>
      <Dialog open={isOpen} onClose={clearForm}>
        <DialogTitle>Create Contact</DialogTitle>
        <DialogBody>
          {errors.saveError && <ErrorMessage>{errors.saveError}</ErrorMessage>}
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    placeholder="First Name"
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    placeholder="Last Name"
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>

              <Field>
                <Label>Role</Label>
                <Input
                  type="text"
                  value={formData.role}
                  placeholder="Role"
                  onChange={(e) => handleInputChange("role", e.target.value)}
                />
                {errors.role && <ErrorMessage>{errors.role}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  placeholder="Email"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Phone Number</Label>
                <PhoneNumberInput
                  defaultValue={formData.phoneNumber}
                  value={formData.phoneNumber}
                  placeholder="Phone Number"
                  onChange={(value) => handleInputChange("phoneNumber", value)}
                />
                {errors.phoneNumber && (
                  <ErrorMessage>{errors.phoneNumber}</ErrorMessage>
                )}
              </Field>

              <SwitchField>
                <Label>Emergency Contact</Label>
                <Switch
                  color="red"
                  checked={formData.isEmergencyContact}
                  onChange={() =>
                    handleInputChange(
                      "isEmergencyContact",
                      !formData.isEmergencyContact,
                    )
                  }
                />
              </SwitchField>

              <SwitchField>
                <Label>Visible to Monitor</Label>
                <Switch
                  checked={formData.isVisibleToSupervisor}
                  onChange={() =>
                    handleInputChange(
                      "isVisibleToSupervisor",
                      !formData.isVisibleToSupervisor,
                    )
                  }
                />
              </SwitchField>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateContactButton;
